import { useEffect, useMemo, useRef, useState } from 'react';
import { CameraFeedMode, Position, SectionDimension } from '../types';
import { getCameraPositionsMap, getRowAndColCount } from '../utils';

export function useMediaLayout(
  alarmCameraIds: string[],
  tenantAdditionalCameraIds: string[],
  globalCamMode: CameraFeedMode,
  showCameraIds: Set<string>,
  preloadCameraIds: Set<string>
) {
  // Reference for the section container
  const sectionRef = useRef<HTMLDivElement>(null);

  // Section dimensions state
  const [sectionDimensions, setSectionDimensions] = useState<SectionDimension>({
    width: 0,
    height: 0,
  });

  // Calculate all cameras to display
  const allCamerasIds: Array<string> = useMemo(() => {
    switch (globalCamMode) {
      case CameraFeedMode.LIVE: {
        return [...alarmCameraIds, ...tenantAdditionalCameraIds];
      }
      case CameraFeedMode.ALARM_VIDEO:
        return [...showCameraIds];
      case CameraFeedMode.SPLIT:
        return alarmCameraIds;
      default:
        return [];
    }
  }, [alarmCameraIds, globalCamMode, showCameraIds, tenantAdditionalCameraIds]);

  // Calculate row and column count
  const [rowCount, colCount] = useMemo(() => {
    const visibleCamCount = allCamerasIds.length;
    return getRowAndColCount(globalCamMode, sectionDimensions, visibleCamCount);
  }, [allCamerasIds, globalCamMode, sectionDimensions]);

  // Calculate camera positions
  const cameraPositionsMap: Record<string, Position> = useMemo(() => {
    return getCameraPositionsMap(
      globalCamMode,
      sectionDimensions,
      allCamerasIds,
      rowCount,
      colCount
    );
  }, [allCamerasIds, colCount, globalCamMode, rowCount, sectionDimensions]);

  // Update section dimensions on resize
  useEffect(() => {
    if (!sectionRef.current) return;

    const updateDimensions = () => {
      if (sectionRef.current) {
        setSectionDimensions({
          width: sectionRef.current.clientWidth,
          height: sectionRef.current.clientHeight,
        });
      }
    };

    // Initial measurement
    updateDimensions();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) {
        resizeObserver.unobserve(sectionRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  // if global cam mode is alarms, return showCameraIds + preloadCameraIds with unique values
  // This will help in rendering cameras which belong to preloadCameraIds and
  // these camera Ids will not have a position in cameraPositionsMap
  const relevantCameraIds = useMemo(() => {
    if (globalCamMode === CameraFeedMode.ALARM_VIDEO) {
      return [...new Set([...showCameraIds, ...preloadCameraIds])];
    }
    return allCamerasIds;
  }, [allCamerasIds, globalCamMode, preloadCameraIds, showCameraIds]);

  return {
    sectionRef,
    sectionDimensions,
    allCamerasIds: relevantCameraIds,
    rowCount,
    colCount,
    cameraPositionsMap,
  };
}
